import { useState, useEffect, useRef } from 'react'
import { searchTags, createTag } from '../lib/api'

interface TagAutocompleteProps {
  selectedTags: string[]
  onChange: (tags: string[]) => void
  disabled?: boolean
}

export const TagAutocomplete = ({ selectedTags, onChange, disabled = false }: TagAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([])
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      try {
        const res = await searchTags(inputValue.trim())
        const names = res.data
          .map((r) => r.tag.name)
          .filter((name) => !selectedTags.includes(name))
        setSuggestions(names)
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
      }
    }, 300)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [inputValue, selectedTags])

  const addTag = async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed || selectedTags.includes(trimmed)) return
    try { await createTag({ name: trimmed }) } catch { /* already exists */ }
    onChange([...selectedTags, trimmed])
    setInputValue('')
    setSuggestions([])
    setShowSuggestions(false)
  }

  const removeTag = (tag: string) => onChange(selectedTags.filter((t) => t !== tag))

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1])
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <div className="tag-input-container">
        {selectedTags.map((tag) => (
          <span key={tag} className="tag-pill">
            #{tag}
            <button type="button" onClick={() => removeTag(tag)} disabled={disabled}>×</button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => inputValue && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? 'Tag eingeben + Enter' : ''}
          disabled={disabled}
        />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="tag-suggestions">
          {suggestions.map((name) => (
            <li key={name}>
              <button type="button" onMouseDown={() => addTag(name)}>{name}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
